import { loadLayeredRelief } from './shapes/layeredEngravingRelief';
import { createLayeredImage } from "./shapes/layeredEngravingImage";
import { createLayeredMesh } from "./shapes/layeredEngravingMesh";
import * as THREE from "three";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { ShapeInterface, Arc, Line, RegPolygon } from "~/types/grid.types";
import { INCH_IN_PIXELS } from "~/composables/grid/constants";
import {
  convertPosition,
  createRectGeometry,
  createPeakGeometry,
  createHexGeometry,
  createPolygonGeometry,
} from "./export3DGeometry";
import { isPeakTile, isUpperPeak } from "./shapes/peakTileCompat";
import {
  createIsoscelesTriangleGeometry,
  createRectTriangleGeometry,
  createArcGeometry,
} from "./export3DTriangles";

export interface Export3DOptions {
  depthMm?: number;
  includeTextures?: boolean;
  bevelEnabled?: boolean;
  wallMounted?: boolean;
}

const DEFAULT_OPTIONS: Export3DOptions = {
  depthMm: 9,
  includeTextures: true,
  bevelEnabled: false,
  wallMounted: true,
};

const Z_LAYER_OFFSET = 0.0001;

export interface GridSize {
  width: number;
  height: number;
}

export const useExport3D = () => {
  const gridStore = useGridStore();

  const reliefCache = new Map<string, ReturnType<typeof loadLayeredRelief>>();
  const materialCache = new Map<string, THREE.MeshStandardMaterial>();

  const createMaterial = async (
    shape: ShapeInterface,
    includeTextures: boolean,
  ): Promise<THREE.MeshStandardMaterial> => {
    const layer = shape.metadata?.layered_engraving;
    if (layer) {
      const key = JSON.stringify(layer);
      if (materialCache.has(key)) return materialCache.get(key)!;
      const material = new THREE.MeshStandardMaterial({ color: includeTextures ? '#ffffff' : layer.top_hex, roughness: 1 });
      if (includeTextures) {
        const image = createLayeredImage(layer, 1000, 1000);
        await new Promise<void>(resolve => { image.onload = () => resolve(); image.onerror = () => resolve(); });
        const texture = new THREE.Texture(image);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        material.map = texture;
      }
      materialCache.set(key, material);
      return material;
    }
    const photoUrl =
      includeTextures && shape.metadata?.photo_url
        ? shape.metadata.photo_url
        : "";
    const cacheKey = photoUrl || `__no_texture_${shape.id}`;

    const cached = materialCache.get(cacheKey);
    if (cached) return cached;

    const material = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      roughness: 1.0,
      metalness: 0.0,
    });

    if (photoUrl) {
      try {
        const textureLoader = new THREE.TextureLoader();
        textureLoader.setCrossOrigin("anonymous");

        const texture = await new Promise<THREE.Texture>((resolve, reject) => {
          textureLoader.load(photoUrl, resolve, undefined, reject);
        });

        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.userData.mimeType = "image/jpeg";

        material.map = texture;
      } catch (error) {
        console.warn(
          "Failed to load texture for shape, using fallback color:",
          error,
        );
        material.color = new THREE.Color(0xaaaaaa);
      }
    } else {
      material.color = new THREE.Color(0xcccccc);
    }

    materialCache.set(cacheKey, material);
    return material;
  };

  const createMeshFromShape = async (
    shape: ShapeInterface,
    options: Export3DOptions,
    gridSize?: GridSize,
  ): Promise<THREE.Mesh | null> => {
    const {
      depthMm: defaultDepthMm = 9,
      includeTextures = true,
      bevelEnabled = true,
    } = options;
    const attrs = shape.attrs || shape;

    let depthMm = defaultDepthMm;
    if (shape.metadata?.depth && typeof shape.metadata.depth === "number") {
      const depthInches = shape.metadata.depth / INCH_IN_PIXELS;
      depthMm = depthInches * 25.4;
    }

    let geometry: THREE.BufferGeometry | null = null;
    const shapeType = shape.type || attrs.type;

    switch (shapeType) {
      case "rect": {
        const width = attrs.width || shape.width;
        const height = attrs.height || shape.height;
        if (width && height) {
          const shapeId = shape.metadata?.shape_id || "";
          const isPeak = isPeakTile(shapeId);
          if (isPeak) {
            const isOffset = isUpperPeak(shapeId);
            geometry = createPeakGeometry(width, height, depthMm, isOffset);
          } else {
            geometry = createRectGeometry(width, height, depthMm, bevelEnabled);
          }
        }
        break;
      }

      case "rect-triangle": {
        const width = attrs.width || shape.width;
        const height = attrs.height || shape.height;
        if (width && height) {
          const flipV = shape.fillPatternScale?.y === -1;
          geometry = createRectTriangleGeometry(width, height, depthMm, flipV);
        }
        break;
      }

      case "hex": {
        const regPoly = shape as RegPolygon;
        const radius = attrs.radius || regPoly.radius;
        const sides = attrs.sides || regPoly.sides || 6;
        if (radius) {
          geometry = createHexGeometry(radius, sides, depthMm, bevelEnabled);
        }
        break;
      }

      case "diamond": {
        const line = shape as Line;
        const points = attrs.points || line.points;
        if (points && points.length >= 6) {
          geometry = createPolygonGeometry(points, depthMm, bevelEnabled);
        }
        break;
      }

      case "triangle": {
        const line = shape as Line;
        const points = attrs.points || line.points;
        if (points && points.length >= 6) {
          geometry = createIsoscelesTriangleGeometry(points, depthMm);
        }
        break;
      }

      case "arc": {
        const arc = shape as Arc;
        const outerRadius = attrs.outerRadius || arc.outerRadius;
        const angle = attrs.angle || arc.angle || 90;
        if (outerRadius) {
          geometry = createArcGeometry(outerRadius, angle, depthMm);
        }
        break;
      }

      default:
        console.warn(`Unknown shape type: ${shapeType}`);
        return null;
    }

    if (!geometry) {
      console.warn("Could not create geometry for shape:", shape);
      return null;
    }

    const material = await createMaterial(shape, includeTextures);
    const layer = shape.metadata?.layered_engraving;
    if (layer && !reliefCache.has(layer.overlay_url)) reliefCache.set(layer.overlay_url, loadLayeredRelief(layer.overlay_url));
    const relief = layer ? await reliefCache.get(layer.overlay_url) : undefined;
    const mesh = layer
      ? createLayeredMesh(attrs.width || shape.width!, attrs.height || shape.height!, layer, material, relief)
      : new THREE.Mesh(geometry, material);
    if (layer) geometry.dispose();

    const x = attrs.x ?? shape.x ?? 0;
    const y = attrs.y ?? shape.y ?? 0;
    const canvasWidth = gridSize?.width ?? gridStore.gridWidth;
    const canvasHeight = gridSize?.height ?? gridStore.gridHeight;
    const position = convertPosition(x, y, canvasWidth, canvasHeight);
    mesh.position.copy(position);

    let rotation = attrs.rotation ?? shape.rotation ?? 0;

    if (shapeType === "hex") {
      rotation = rotation - 90;
    }

    mesh.rotation.z = -(rotation * Math.PI) / 180;

    return mesh;
  };

  const createScene = (): THREE.Scene => {
    const scene = new THREE.Scene();
    scene.background = null;
    return scene;
  };

  const exportSceneToGLB = async (scene: THREE.Scene): Promise<ArrayBuffer> => {
    const exporter = new GLTFExporter();

    const result = await exporter.parseAsync(scene, {
      binary: true,
    });

    if (result instanceof ArrayBuffer) {
      return result;
    } else {
      throw new Error(
        "Expected binary GLB output but received JSON. Check Three.js GLTFExporter configuration.",
      );
    }
  };

  const exportToGLB = async (
    shapesOrOptions?: ShapeInterface[] | Export3DOptions,
    gridSizeOrOptions?: GridSize | Export3DOptions,
    opts?: Export3DOptions,
  ): Promise<ArrayBuffer> => {
    let shapes: ShapeInterface[];
    let gridSize: GridSize | undefined;
    let mergedOptions: Export3DOptions;

    if (Array.isArray(shapesOrOptions)) {
      shapes = shapesOrOptions;
      gridSize = gridSizeOrOptions as GridSize | undefined;
      mergedOptions = { ...DEFAULT_OPTIONS, ...opts };
    } else {
      shapes = gridStore.shapeList;
      gridSize = undefined;
      mergedOptions = { ...DEFAULT_OPTIONS, ...shapesOrOptions };
    }

    if (shapes.length === 0) {
      throw new Error("No shapes to export");
    }

    materialCache.clear();

    const scene = createScene();

    const designGroup = new THREE.Group();
    designGroup.name = "FeltRightDesign";

    const meshEntries: Array<{
      mesh: THREE.Mesh;
      shape: ShapeInterface;
      index: number;
    }> = [];
    for (let i = 0; i < shapes.length; i++) {
      const shape = shapes[i];
      try {
        const mesh = await createMeshFromShape(shape, mergedOptions, gridSize);
        if (mesh) {
          meshEntries.push({ mesh, shape, index: i });
        }
      } catch (error) {
        console.error("Failed to convert shape:", shape.id, error);
      }
    }

    const OVERLAP_EPSILON = 0.0005;
    const bounds = meshEntries.map(({ mesh }) => {
      const box = new THREE.Box3().setFromObject(mesh);
      return {
        minX: box.min.x + OVERLAP_EPSILON,
        maxX: box.max.x - OVERLAP_EPSILON,
        minY: box.min.y + OVERLAP_EPSILON,
        maxY: box.max.y - OVERLAP_EPSILON,
      };
    });

    for (let i = 0; i < meshEntries.length; i++) {
      const a = bounds[i];
      let overlaps = false;
      for (let j = 0; j < i; j++) {
        const b = bounds[j];
        if (
          a.minX < b.maxX &&
          a.maxX > b.minX &&
          a.minY < b.maxY &&
          a.maxY > b.minY
        ) {
          overlaps = true;
          break;
        }
      }
      if (overlaps) {
        meshEntries[i].mesh.position.z += meshEntries[i].index * Z_LAYER_OFFSET;
      }
    }

    if (meshEntries.length === 0) {
      throw new Error("Failed to convert any shapes to 3D");
    }

    const materialGroups = new Map<string, typeof meshEntries>();
    for (const entry of meshEntries) {
      const photoUrl = entry.shape.metadata?.photo_url || "";
      const key = entry.shape.metadata?.layered_engraving ? `layered_${entry.index}` : photoUrl || `__no_texture_${entry.index}`;
      if (!materialGroups.has(key)) {
        materialGroups.set(key, []);
      }
      materialGroups.get(key)!.push(entry);
    }

    let finalMeshCount = 0;
    for (const [, entries] of materialGroups) {
      if (entries.length === 1) {
        const { mesh } = entries[0];
        mesh.name = `shape_${finalMeshCount}`;
        designGroup.add(mesh);
        finalMeshCount++;
        continue;
      }

      const geometriesToMerge: THREE.BufferGeometry[] = [];
      let sharedMaterial: THREE.MeshStandardMaterial | null = null;

      for (const { mesh } of entries) {
        mesh.updateMatrix();
        const geom = mesh.geometry.clone();
        geom.applyMatrix4(mesh.matrix);
        geometriesToMerge.push(geom);

        if (!sharedMaterial) {
          sharedMaterial = mesh.material as THREE.MeshStandardMaterial;
        }
      }

      try {
        const mergedGeometry = mergeGeometries(geometriesToMerge, false);
        if (mergedGeometry && sharedMaterial) {
          const mergedMesh = new THREE.Mesh(mergedGeometry, sharedMaterial);
          mergedMesh.name = `merged_${finalMeshCount}`;
          designGroup.add(mergedMesh);
          finalMeshCount++;
        } else {
          for (const { mesh } of entries) {
            mesh.name = `shape_${finalMeshCount}`;
            designGroup.add(mesh);
            finalMeshCount++;
          }
        }
      } catch (error) {
        console.warn("Geometry merge failed, using individual meshes:", error);
        for (const { mesh } of entries) {
          mesh.name = `shape_${finalMeshCount}`;
          designGroup.add(mesh);
          finalMeshCount++;
        }
      }

      for (const geom of geometriesToMerge) {
        geom.dispose();
      }
    }

    const boundingBox = new THREE.Box3().setFromObject(designGroup);
    const center = boundingBox.getCenter(new THREE.Vector3());

    designGroup.children.forEach((child) => {
      child.position.x -= center.x;
      child.position.y -= center.y;
    });

    scene.add(designGroup);

    const glbData = await exportSceneToGLB(scene);

    return glbData;
  };

  const prepareForAR = async (
    data: ArrayBuffer,
  ): Promise<{ glbUrl: string; usdzUrl: string; modelId: string }> => {
    let body: ArrayBuffer | Uint8Array = data;
    let contentEncoding: string | undefined;
    try {
      const stream = new Blob([data])
        .stream()
        .pipeThrough(new CompressionStream("gzip"));
      const compressed = await new Response(stream).arrayBuffer();
      body = compressed;
      contentEncoding = "gzip";
    } catch {
      // CompressionStream not available, send uncompressed
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/octet-stream",
    };
    if (contentEncoding) {
      headers["Content-Encoding"] = contentEncoding;
    }

    const response = await fetch("/api/ar/generate-model", {
      method: "POST",
      headers,
      body,
    });

    if (!response.ok) {
      throw new Error("Failed to upload model for AR viewing");
    }

    const result = await response.json();
    const { glbUrl, usdzUrl, id: modelId } = result;

    return { glbUrl, usdzUrl, modelId };
  };

  const launchARDirectly = (_glbUrl: string, _usdzUrl: string): boolean => {
    return false;
  };

  return {
    exportToGLB,
    prepareForAR,
    launchARDirectly,
  };
};
